import { createTheme } from '@mui/material/styles';

// Brand colors from tailwind.config.js / clinicui reference
const brand = {
  // Counted from clinicui's own source: #366A75 appears 320 times, #EBF5F5 104,
  // #B4D9D4 101, #D0E7E5 38, #B5D9D4 20 (its table headers), #2D5A60 16.
  // Those are the surfaces a coordinator recognises, so the portal uses them.
  tableHead: '#B5D9D4',
  orange: '#F28E2E',
  teal: '#3A7380',
  darkTeal: '#284C58',
  mint: '#B8DDD0',
  modalBg: '#B4D9D4',
  modalText: '#5B7F8A',
  titleText: '#366A75',
  fieldBg: '#EBF5F5',
  buttonHover: '#D0E7E5',
  closeBtnHover: '#2D5A60',
};

const theme = createTheme({
  direction: 'rtl',
  palette: {
    primary: {
      main: brand.teal,        // #3A7380 — clinicui brand teal
      // Page headings read primary.dark. clinicui's own headings are #366A75,
      // which it uses 320 times - #284C58 appears there 19. One change here
      // moves every screen rather than seven edits across the pages.
      dark: brand.titleText,   // #366A75
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: brand.orange,      // #F28E2E — clinicui brand orange
      contrastText: '#FFFFFF',
    },
    success: { main: '#10B981' },
    error: { main: '#EF4444' },
    warning: { main: '#F59E0B' },
    info: { main: '#06B6D4' },
    text: {
      primary: '#1F2937',
      secondary: '#6B7280',
      disabled: '#9CA3AF',
    },
    background: {
      default: '#F9FAFB',
      paper: '#FFFFFF',
    },
    divider: '#E5E7EB',
    action: {
      hover: 'rgba(58, 115, 128, 0.04)',    // subtle teal tint on hover
      selected: 'rgba(58, 115, 128, 0.08)',
    },
  },

  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    button: {
      textTransform: 'none' as const,
      fontWeight: 500,
    },
  },

  shape: {
    borderRadius: 8, // clinicui default 0.5rem
  },

  components: {
    /* ── CssBaseline ─────────────────────────────── */
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#F9FAFB',
        },
      },
    },

    /* ── Buttons ─────────────────────────────────── */
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          textTransform: 'none' as const,
          padding: '8px 16px',
        },
        startIcon: {
          marginRight: -4,
          marginLeft: 8,
        },
        endIcon: {
          marginLeft: -4,
          marginRight: 8,
        },
        containedPrimary: {
          // clinicui buttons are the light field colour with a teal label, not
          // a solid teal block - bg-[#EBF5F5] appears 101 times there.
          backgroundColor: brand.fieldBg,
          color: brand.teal,
          boxShadow: 'none',
          '&:hover': { backgroundColor: brand.buttonHover, boxShadow: 'none' },
        },
        containedSecondary: {
          backgroundColor: brand.orange,
          '&:hover': { backgroundColor: '#D97A1F' },
        },
        outlinedPrimary: {
          borderColor: brand.teal,
          color: brand.teal,
          '&:hover': {
            backgroundColor: 'rgba(58, 115, 128, 0.04)',
            borderColor: brand.darkTeal,
          },
        },
      },
    },

    /* ── IconButton ──────────────────────────────── */
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(58, 115, 128, 0.08)',
          },
        },
      },
    },

    /* ── Text fields / inputs ────────────────────── */
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: brand.teal,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: brand.teal,
            },
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          textAlign: 'right',
        },
        input: {
          textAlign: 'right',
        },
        root: {
          // clinicui fields are rounded-[20px].
          borderRadius: 20,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: brand.teal,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: brand.teal,
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: brand.teal,
          },
          // Anchor the label to the right edge and let it grow leftwards.
          left: 'auto',
          right: 0,
          transformOrigin: 'top right',
        },
        outlined: {
          transform: 'translate(-14px, 9px) scale(1)',
          '&.MuiInputLabel-shrink': {
            transform: 'translate(-14px, -9px) scale(0.75)',
          },
        },
      },
    },

    /* ── Select ──────────────────────────────────── */
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },

    /* ── Chip / Badge ────────────────────────────── */
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          fontSize: '0.75rem',
        },
        colorPrimary: {
          backgroundColor: 'rgba(58, 115, 128, 0.1)',
          color: brand.teal,
        },
        colorSecondary: {
          backgroundColor: 'rgba(242, 142, 46, 0.1)',
          color: brand.orange,
        },
        // MUI paints warning/success/error/info as SOLID chips with white text.
        // clinicui paints a status the other way round, everywhere
        // it paints one - `px-2 py-1 text-xs rounded bg-*-100 text-*-700`, a
        // pale tint carrying dark text, and never a filled block. The lead
        // statuses were reaching a coordinator as solid amber and solid green
        // beside a page that is otherwise soft teal, which is the one thing on
        // the screen that did not look like the system she already knows.
        //
        // The pairs below are clinicui's own, read off UsersScreen: it uses
        // green for on, gray for off, yellow for provisional and blue for the
        // real thing. Kept as literals rather than folded into `brand` because
        // they are semantic states, not brand surfaces - the teal palette says
        // whose product this is, these say what happened to the referral.
        colorSuccess: {
          backgroundColor: '#DCFCE7',   // green-100
          color: '#15803D',             // green-700
        },
        colorWarning: {
          backgroundColor: '#FEF9C3',   // yellow-100
          color: '#A16207',             // yellow-700
        },
        colorError: {
          backgroundColor: '#FEE2E2',   // red-100
          color: '#B91C1C',             // red-700
        },
        colorInfo: {
          backgroundColor: '#DBEAFE',   // blue-100
          color: '#1D4ED8',             // blue-700
        },
        // No slot for the default colour, and none wanted: MUI already paints an
        // uncoloured chip rgba(0,0,0,0.08) with dark text, which is clinicui's
        // gray-100/gray-700 pill in all but the exact hex. Overriding it would
        // also have to go on the `filled` slot, and the resolver emits that
        // AFTER every `color*` slot above (Chip.js:75) - so the gray would have
        // won over the green and the yellow, which is the opposite of the point.
      },
    },

    /* ── Paper / Card ────────────────────────────── */
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none', // remove MUI's default gradient overlay
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)',
        },
        elevation2: {
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06)',
        },
      },
    },

    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          border: '1px solid #E5E7EB',
          borderRadius: 12,
        },
      },
    },

    /* ── Table ───────────────────────────────────── */
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: '#E5E7EB',
          padding: '12px 16px',
          fontSize: '0.875rem',
        },
        head: {
          backgroundColor: brand.tableHead,
          color: brand.titleText,
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase' as const,
          letterSpacing: '0.05em',
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            // Same family as the header band, several shades up.
            backgroundColor: 'rgba(180, 217, 212, 0.18)',
          },
        },
      },
    },

    /* ── Dialog / Modal ──────────────────────────── */
    /* Dialogs follow clinicui's popup palette (src/constants/popupStyles.ts
       there): the teal ground with the fields and buttons a shade lighter on
       top of it. A coordinator moves between the two apps, and a popup that
       arrives white reads as a different product. Scoped to the dialog paper
       on purpose - the rest of the portal keeps its own surfaces. */
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: brand.modalBg,
          borderRadius: 24,
          padding: '4px 8px 8px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',

          // Fields sit lighter on the teal, as they do in clinicui.
          '& .MuiOutlinedInput-root': {
            backgroundColor: brand.fieldBg,
            borderRadius: 8,
            '& fieldset': { borderColor: brand.modalBg },
            '&:hover fieldset': { borderColor: brand.titleText },
            '&.Mui-focused fieldset': { borderColor: brand.titleText },
          },
          '& .MuiInputLabel-root': { color: brand.modalText },
          '& .MuiInputLabel-root.Mui-focused': { color: brand.titleText },
          '& .MuiFormHelperText-root': { color: brand.modalText },

          // Both buttons share the field background; only the label separates
          // them, which is how clinicui distinguishes them too.
          '& .MuiDialogActions-root .MuiButton-root': {
            backgroundColor: brand.fieldBg,
            color: brand.teal,
            borderRadius: 12,
            paddingInline: 32,
            boxShadow: 'none',
            '&:hover': { backgroundColor: brand.buttonHover, boxShadow: 'none' },
          },
          '& .MuiIconButton-root': {
            color: brand.titleText,
            '&:hover': { color: brand.closeBtnHover },
          },
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.5rem',
          fontWeight: 700,
          color: brand.titleText,
          textAlign: 'center',
          paddingBottom: 20,
        },
      },
    },

    /* ── Alert ───────────────────────────────────── */
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        standardSuccess: {
          backgroundColor: '#ECFDF5',
          color: '#065F46',
          border: '1px solid #A7F3D0',
        },
        standardError: {
          backgroundColor: '#FEF2F2',
          color: '#991B1B',
          border: '1px solid #FECACA',
        },
        standardWarning: {
          backgroundColor: '#FFFBEB',
          color: '#92400E',
          border: '1px solid #FDE68A',
        },
        standardInfo: {
          backgroundColor: '#ECFEFF',
          color: '#155E75',
          border: '1px solid #A5F3FC',
        },
      },
    },

    /* ── Tabs ────────────────────────────────────── */
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontWeight: 500,
          '&.Mui-selected': {
            color: brand.teal,
          },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: brand.teal,
        },
      },
    },

    /* ── Tooltip ─────────────────────────────────── */
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1F2937',
          fontSize: '0.75rem',
          borderRadius: 6,
        },
      },
    },

    /* ── Autocomplete ────────────────────────────── */
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
        },
      },
    },

    /* ── Menu ────────────────────────────────────── */
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
          border: '1px solid #E5E7EB',
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          '&:hover': {
            backgroundColor: '#F9FAFB',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(58, 115, 128, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(58, 115, 128, 0.12)',
            },
          },
        },
      },
    },

    /* ── Switch ──────────────────────────────────── */
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: brand.teal,
          },
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: brand.teal,
          },
        },
      },
    },

    /* ── Checkbox / Radio ────────────────────────── */
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: brand.teal,
          },
        },
      },
    },

    MuiRadio: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: brand.teal,
          },
        },
      },
    },

    /* ── CircularProgress ────────────────────────── */
    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: {
          color: brand.teal,
        },
      },
    },

    /* ── LinearProgress ──────────────────────────── */
    MuiLinearProgress: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: 'rgba(58, 115, 128, 0.1)',
        },
        barColorPrimary: {
          backgroundColor: brand.teal,
        },
      },
    },

    /* ── TablePagination ─────────────────────────── */
    MuiTablePagination: {
      styleOverrides: {
        actions: {
          direction: 'ltr',
          '& button svg': {
            transform: 'scaleX(-1)',
          },
        },
      },
    },

    /* ── Pagination ──────────────────────────────── */
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: brand.teal,
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: brand.darkTeal,
            },
          },
        },
      },
    },

    /* ── Fab ─────────────────────────────────────── */
    MuiFab: {
      styleOverrides: {
        primary: {
          backgroundColor: brand.teal,
          '&:hover': { backgroundColor: brand.darkTeal },
        },
      },
    },
  },
});

export default theme;
